import React, { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import LMSFeedbackAnalytics from './LMSFeedbackAnalytics';
import RawFeedbackDownloadDropdown from './RawFeedbackDownloadDropdown';
import { downloadRawFeedbackPdf } from '../../utils/rawFeedbackPdfExport';
import { downloadRawFeedbackExcel } from '../../utils/rawFeedbackExcelExport';

const PlatformFeedbackTab = ({
  lmsFeedbacks = [],
  universities = null,
  platformForms = [],
  lmsSchema,
  renderRawFeedback,
  showToast
}) => {
  const [viewMode, setViewMode] = useState('analytics');
  const [selectedUniversity, setSelectedUniversity] = useState('All');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [downloadingRaw, setDownloadingRaw] = useState(false);
  const [downloadingRawExcel, setDownloadingRawExcel] = useState(false);

  const hasFeedbackContent = (obj) => {
    if (!obj) return false;
    if (typeof obj === 'string') {
      try {
        const parsed = JSON.parse(obj);
        return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
      } catch (_) {
        return false;
      }
    }
    return typeof obj === 'object' && Object.keys(obj).length > 0;
  };

  // Only consider items that actually belong to Platform Feedback (have platform answers or are platform form submissions)
  const platformSubmissions = lmsFeedbacks.filter(f => {
    const isPlatformForm = f.form_id && String(f.form_id).startsWith('platform_feedback');
    const hasPlatformAnswers = hasFeedbackContent(f.platform_feedback);
    return isPlatformForm || hasPlatformAnswers;
  });

  const filteredFeedbacks = platformSubmissions.filter(f => 
    selectedUniversity === 'All' || f.university_name === selectedUniversity
  );

  const availableUniversities = [
    ...new Set([
      ...(universities || []),
      ...platformSubmissions.map(f => f.university_name)
    ])
  ].filter(Boolean).sort();

  const handleDownloadRawPlatformExcel = async () => {
    if (!filteredFeedbacks || filteredFeedbacks.length === 0) {
      const msg = selectedUniversity !== 'All'
        ? `No platform feedback submissions found for ${selectedUniversity}.`
        : 'No platform feedback submissions available to export.';
      if (showToast) showToast(msg, 'info');
      else alert(msg);
      return;
    }
    setDownloadingRawExcel(true);
    try {
      await downloadRawFeedbackExcel({
        feedbacks: filteredFeedbacks,
        reportType: 'platform',
        universityName: selectedUniversity,
        courseForms: [],
        platformForms,
        lmsSchema
      });
      if (showToast) {
        showToast('Platform Raw Feedback Excel sheet downloaded successfully.');
      }
    } catch (err) {
      console.error('Error generating raw platform feedback Excel:', err);
      if (showToast) {
        showToast('Failed to generate Excel report. Please try again.', 'error');
      } else {
        alert('Failed to generate Excel report. Please try again.');
      }
    } finally {
      setDownloadingRawExcel(false);
    }
  };

  const handleDownloadRawPlatformReport = async () => {
    if (!filteredFeedbacks || filteredFeedbacks.length === 0) {
      const msg = selectedUniversity !== 'All'
        ? `No platform feedback submissions found for ${selectedUniversity}.`
        : 'No platform feedback submissions available to export.';
      if (showToast) showToast(msg, 'info');
      else alert(msg);
      return;
    }
    setDownloadingRaw(true);
    try {
      await downloadRawFeedbackPdf({
        feedbacks: filteredFeedbacks,
        reportType: 'platform',
        universityName: selectedUniversity,
        courseForms: [],
        platformForms,
        lmsSchema
      });
      if (showToast) {
        showToast('Platform Raw Feedback PDF Report downloaded successfully.');
      }
    } catch (err) {
      console.error('Error generating raw platform feedback PDF:', err);
      if (showToast) {
        showToast('Failed to generate PDF report. Please try again.', 'error');
      } else {
        alert('Failed to generate PDF report. Please try again.');
      }
    } finally {
      setDownloadingRaw(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 className="chart-title" style={{ margin: 0 }}>Platform Feedbacks</h3>
        <div style={{ display: 'flex', background: 'var(--bg-main)', borderRadius: '8px', padding: '4px', border: '1px solid var(--border)' }}>
          <button 
            onClick={() => setViewMode('analytics')}
            style={{ padding: '6px 16px', background: viewMode === 'analytics' ? 'var(--primary)' : 'transparent', color: viewMode === 'analytics' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >
            Analytics View
          </button>
          <button 
            onClick={() => setViewMode('raw')}
            style={{ padding: '6px 16px', background: viewMode === 'raw' ? 'var(--primary)' : 'transparent', color: viewMode === 'raw' ? '#fff' : 'var(--text-main)', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
          >
            Raw Data
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', background: 'var(--bg-main)', padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>University:</span>
          <select 
            value={selectedUniversity} 
            onChange={e => setSelectedUniversity(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', fontSize: '0.85rem', minWidth: '120px' }}
          >
            <option value="All">All Universities</option>
            {availableUniversities.map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>

        {viewMode === 'raw' && (
          <RawFeedbackDownloadDropdown
            onDownloadPdf={handleDownloadRawPlatformReport}
            onDownloadExcel={handleDownloadRawPlatformExcel}
            isDownloadingPdf={downloadingRaw}
            isDownloadingExcel={downloadingRawExcel}
            universityName={selectedUniversity}
            disabled={filteredFeedbacks.length === 0}
          />
        )}
      </div>

      {viewMode === 'analytics' ? (
        <LMSFeedbackAnalytics courseId="All" universityName={selectedUniversity} displayType="platform" />
      ) : (
        <>
          {filteredFeedbacks.length === 0 ? (
            <div className="admin-empty-state">
              <div className="empty-icon"><MessageSquare size={48} /></div>
              <h3>No feedback submitted yet</h3>
            </div>
          ) : (
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Student</th>
                  <th>University</th>
                  <th>Course / Form</th>
                  <th>Batch</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeedbacks.map(item => (
                  <tr key={item.id}>
                    <td>{new Date(item.created_at).toLocaleDateString('en-GB')}</td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{item.student_name}</div>
                      {(item.usn || item.student_email) && (
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {item.usn && <span style={{ marginRight: '6px', fontFamily: 'monospace' }}>{item.usn}</span>}
                          {item.student_email && <span>{item.student_email}</span>}
                        </div>
                      )}
                    </td>
                    <td>{item.university_name || 'Unknown'}</td>
                    <td>
                      {item.course_name ? `${item.course_name} (${item.course_id})` : item.course_id}
                      {item.form_title && (
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          Form: {item.form_title}
                        </div>
                      )}
                    </td>
                    <td>{item.batch_name || 'Unknown Batch'}</td>
                    <td>
                      <button 
                        onClick={() => setSelectedFeedback(item)} 
                        style={{ padding: '6px 16px', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-main)', cursor: 'pointer', fontWeight: 600 }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Feedback Details Modal */}
          {selectedFeedback && (
            <div className="modal-overlay" onClick={() => setSelectedFeedback(null)}>
              <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--text-main)' }}>Feedback Details - {selectedFeedback.student_name}</h2>
                  <button 
                    onClick={() => setSelectedFeedback(null)}
                    style={{ background: 'transparent', border: '1px solid var(--border)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-main)' }}
                  >
                    Close
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }}>
                  <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <h3 style={{ fontSize: '1.1rem', color: 'var(--primary)', marginBottom: '16px', borderBottom: '2px solid var(--bg-main)', paddingBottom: '12px' }}>Platform Feedback</h3>
                    {renderRawFeedback(selectedFeedback.platform_feedback, ((platformForms.find(pf => pf.id === (selectedFeedback.form_id || selectedFeedback.course_id))?.questions) || platformForms[0]?.questions || lmsSchema?.platform_feedback), true)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PlatformFeedbackTab;
